import React, { useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Mail,
  Phone,
  Clock,
  FileText,
  ChevronDown,
  RefreshCcw,
  CreditCard,
  Building,
} from "lucide-react";
import CtaSection from "../CtaSection/CtaSection";

const RefundPage = () => {
  const [openSections, setOpenSections] = useState([1]);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const sections = [
    {
      id: 1,
      title: "Refund & Cancellation Policy",
      icon: CreditCard,
      subsections: [
        {
          subtitle: "Introduction",
          items: [
            "This Refund and Cancellation Policy governs all payments and transactions made through the Bumppy Payments platform.",
            "By using our services, you acknowledge that you have read, understood, and agreed to this Refund Policy.",
            "All refund requests are subject to verification and approval by Bumppy Payments in accordance with applicable laws and payment partner guidelines.",
          ],
        },
        {
          subtitle: "Refund Eligibility",
          items: [
            "Refunds may be approved in cases of duplicate payments.",
            "Refunds may be processed where an amount is debited but the transaction has failed due to a verified technical error.",
            "Refunds may be considered where a service could not be delivered due to reasons attributable to Bumppy Payments.",
            "Refund requests must be supported with valid transaction details and proof of payment.",
          ],
        },
        {
          subtitle: "Non-Refundable Transactions",
          items: [
            "Successful transactions where the requested service has already been delivered are not eligible for refunds.",
            "Convenience fees, platform charges, taxes, and other statutory charges are non-refundable unless required by applicable law.",
            "Refunds shall not be provided for user errors, including incorrect beneficiary details, wrong mobile numbers, incorrect account information, or transactions initiated with inaccurate information.",
          ],
        },
        {
          subtitle: "Refund Process",
          items: [
            "Users may raise a refund request by contacting our customer support team with the transaction reference number, registered mobile number, and reason for the refund.",
            "All refund requests are subject to verification before approval.",
            "Approved refunds shall be credited only to the original payment source unless otherwise required by applicable law.",
          ],
        },
        {
          subtitle: "Refund Timelines",
          items: [
            "Once approved, refunds are generally processed within 5–7 business days.",
            "The actual credit timeline depends upon the customer's bank, card issuer, UPI provider, or payment service provider.",
            "Delays caused by banking networks or payment partners are beyond the control of Bumppy Payments.",
          ],
        },
        {
          subtitle: "Cancellation Policy",
          items: [
            "Cancellation requests can only be accepted before the requested service has been successfully processed.",
            "Once a transaction has been successfully completed or a service has been delivered, cancellation requests cannot be entertained.",
            "Bumppy Payments reserves the right to reject cancellation requests that do not satisfy the conditions specified in this Policy.",
          ],
        },
        {
          subtitle: "Failed & Duplicate Transactions",
          items: [
            "If your account has been debited but the transaction status shows 'Failed' or 'Pending', please wait for confirmation from the banking network.",
            "Eligible failed or duplicate transactions are automatically reconciled and refunded after successful verification.",
            "Users are advised not to initiate multiple payments for the same transaction until the previous transaction status has been confirmed.",
          ],
        },
        {
          subtitle: "Chargebacks & Disputes",
          items: [
            "Users should first contact Bumppy Payments before initiating a chargeback with their bank or card issuer.",
            "Fraudulent or unauthorized chargeback requests may result in suspension or termination of the user account.",
            "Any dispute relating to refunds shall be governed by the Terms & Conditions of Bumppy Payments and applicable laws of India.",
          ],
        },
        {
          subtitle: "Contact for Refund Requests",
          items: [
            "Email: support@bumppy.com",
            "Website: https://payments.bumppy.com",
            "Address: B-218, Ithum Tower, Sector-62, Noida - 201301, Uttar Pradesh, India",
            "Please include your Transaction ID, registered mobile number, payment date, and reason for the refund request to facilitate faster resolution.",
          ],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased">
      <header className="border-b border-slate-200 bg-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Refund & Cancellation Policy
          </h1>
          <p className="mt-2 text-base text-slate-600 max-w-2xl leading-relaxed">
            We believe in fair transactions. This policy outlines the terms for
            cancellations and refund processing via our partner Razorpay.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        {sections.map((section) => {
          const Icon = section.icon;
          const isOpen = openSections.includes(section.id);

          return (
            <article
              key={section.id}
              className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full text-left p-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex items-center gap-3.5">
                  <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                    <Icon size={20} />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900">
                    {section.id}. {section.title}
                  </h2>
                </div>
                <ChevronDown
                  size={18}
                  className={`text-slate-400 transition-transform duration-200 ${
                    isOpen ? "rotate-180 text-slate-600" : ""
                  }`}
                />
              </button>

              {isOpen && (
                <div className="px-5 pb-6 pt-2 border-t border-slate-100 space-y-6">
                  {section.subsections.map((sub, i) => (
                    <div key={i} className="space-y-2.5">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                        {sub.subtitle}
                      </h3>
                      <ul className="space-y-2">
                        {sub.items.map((item, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-600 leading-relaxed"
                          >
                            <CheckCircle2
                              size={15}
                              className="text-emerald-500 shrink-0 mt-0.5"
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-6">
          <div className="flex items-center gap-3.5">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
              <FileText size={20} />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">
                How to Request
              </h2>
              <p className="text-xs text-slate-500">
                Follow these steps for a quick resolution
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="mailto:payments@bumppy.com"
              className="p-4 rounded-lg bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors flex items-center gap-3.5"
            >
              <Mail className="text-blue-600 shrink-0" size={20} />
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Email Us
                </p>
                <p className="text-xs font-semibold text-slate-900 truncate">
                  support@bumppy.com
                </p>
              </div>
            </a>

            <a
              href="tel:8130096175"
              className="p-4 rounded-lg bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors flex items-center gap-3.5"
            >
              <Phone className="text-blue-600 shrink-0" size={20} />
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Call Support
                </p>
                <p className="text-xs font-semibold text-slate-900">
                  +91 8130096176
                </p>
              </div>
            </a>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 mb-3">
              Required Information:
            </h4>
            <div className="flex flex-wrap gap-2">
              {[
                "Transaction ID",
                "Payment Date",
                "Amount",
                "Reason for Refund",
              ].map((item) => (
                <span
                  key={item}
                  className="px-3 py-1 bg-slate-100 text-slate-700 text-xs rounded-full font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 text-slate-500 text-xs">
              <Clock size={14} />
              <span>Response time: Usually within 3 business days.</span>
            </div>
          </div>
        </div>

        <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
          <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <p className="text-xs sm:text-sm text-amber-900 leading-relaxed">
            <strong>Note:</strong> Bumppy Payments reserves the right to modify
            this policy at any time. Any changes will be updated here and will
            apply to all subsequent transactions.
          </p>
        </div>
      </main>

      <CtaSection />

      <style>{`
        .animate-spin-slow {
          animation: spin 8s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RefundPage;
